<!--
  - Copyright (c) 2019 brightlamp
  -->

<template>
    <div>
        <v-toolbar dense flat color="white">
            <v-toolbar-title>Org Users</v-toolbar-title>
            <v-spacer/>
            <v-text-field
                    v-model="search"
                    append-icon="mdi-magnify"
                    label="Search"
                    single-line
                    hide-details
                    clearable
                    @click:clear="handleClearSearch"
                    v-debounce:300="handleSearch">
            </v-text-field>
            <v-spacer/>
            <v-icon large class="mr-2" @click="loadItems">
                mdi-refresh
            </v-icon>
            <v-dialog v-model="dialog" max-width="500px">
                <template v-slot:activator="{ on }">
                    <v-btn color="accent" v-on="on">
                        <span style="color: #000000;" @click="newUser">New User</span>
                    </v-btn>
                </template>
                <v-card>
                    <v-card-title>
                        <span class="headline" align="center" style="color:#FFFD00;background-color:#363636;width:100%;display:block;">{{ formTitle }}</span>
                    </v-card-title>
                    <v-card-text>
                        <v-progress-linear v-if="waitingOnServer" :indeterminate="true"/>
                        <v-container>
                            <v-row dense>
                                <v-col md="12">
                                    <v-text-field
                                            v-model="userData.displayName"
                                            label="Display Name"
                                            :clearable="!readonly"
                                            single-line
                                            :readonly="readonly">
                                    </v-text-field>
                                </v-col>
                                <v-col md="12">
                                    <v-row dense>
                                        <v-text-field
                                                v-model="userData.emailAddress"
                                                label="Email Address"
                                                :clearable="!readonly"
                                                single-line
                                                :readonly="readonly">
                                        </v-text-field>
                                        <v-checkbox v-if="this.editedIndex !== -1" class="ml-5"
                                                    type="checkbox"
                                                    label="Update?"
                                                    v-model="updateEmailAddress"
                                                    :disabled="readonly"
                                                    color="primary"/>
                                    </v-row>
                                </v-col>
                                <v-col md="12">
                                    <v-select
                                            v-if="currentUser && currentUser.authorities && currentUser.authorities[0].role === 'ROLE_ADMIN'"
                                            v-model="userData.role"
                                            :items="userRoles"
                                            label="User Role"
                                            single-line
                                            :readonly="readonly">
                                    </v-select>
                                </v-col>
                                <v-col md="12">
                                    <v-select
                                            v-model="userData.maxAppLevel"
                                            :items="maxAppLevels"
                                            label="Max App Level"
                                            single-line
                                            :readonly="readonly">
                                    </v-select>
                                </v-col>
                                <v-col md="12">
                                    <v-select
                                        v-model="userData.testProtoSuite"
                                        :items="testProtocolSuiteNames"
                                        label="Testing Protocol Suite"
                                        single-line
                                        :readonly="readonly">
                                    </v-select>
                                </v-col>
                            </v-row>
                        </v-container>
                        <v-container>
                            <v-row>
                                <v-col md="12">
                                    <strong>Total PLR Tests Taken:</strong> {{ totalPlrTestsForUser }}
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col md="12">
                                    <strong>Total NPC Tests Taken:</strong> {{ totalNpcTestsForUser }}
                                </v-col>
                            </v-row>
                        </v-container>
                    </v-card-text>
                    <v-card-actions class="mr-2">
                        <v-spacer/>
                        <v-btn color="blue darken-1" text @click="closeDialog">Cancel</v-btn>
                        <v-btn color="blue darken-1" text :disabled="readonly" @click="save">Save</v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>
        </v-toolbar>
        <v-container fluid>
            <v-row no-gutters>
                <v-col>
                    <v-data-table
                        :headers="headers"
                        :items="users"
                        item-key="_id"
                        @current-items="currentItems"
                        :options.sync="pagination"
                        :server-items-length="totalRecords"
                        :loading="waiting"
                        :footer-props="{'items-per-page-options':[15, 30, 50, 100]}"
                        class="elevation-1"
                        @update:options="loadItems">
                        <template v-slot:item.actions="{ item }">
                            <v-tooltip bottom color="black" open-delay="800" :disabled=tipDisabling.viewing>
                                <template #activator="{ on }">
                                        <span v-on="on">
                                            <v-icon class="mr-2" small @click="viewItem(item)">
                                                mdi-compass
                                            </v-icon>
                                        </span>
                                </template>
                                <span>View</span>
                            </v-tooltip>
                            <v-tooltip bottom color="black" open-delay="800" :disabled=tipDisabling.editing>
                                <template #activator="{ on }">
                                        <span v-on="on">
                                            <v-icon class="mr-2" small @click="editItem(item)">
                                                mdi-pencil
                                            </v-icon>
                                        </span>
                                </template>
                                <span>Edit</span>
                            </v-tooltip>
                            <v-tooltip bottom color="black" open-delay="800" :disabled=tipDisabling.deleting>
                                <template #activator="{ on }">
                                        <span v-on="on">
                                            <v-icon class="mr-2" small @click="deleteItem(item)">
                                                mdi-delete
                                            </v-icon>
                                        </span>
                                </template>
                                <span>Delete</span>
                            </v-tooltip>
                            <v-tooltip bottom color="black" open-delay="800" :disabled=tipDisabling.emailing>
                                <template #activator="{ on }">
                                        <span v-on="on">
                                            <v-icon class="mr-2" small @click="sendEmailItem(item)">
                                                mdi-email
                                            </v-icon>
                                        </span>
                                </template>
                                <span>Email</span>
                            </v-tooltip>
                        </template>
                    </v-data-table>
                </v-col>
            </v-row>
        </v-container>
        <ConfirmationDialog v-if="confirmDeleteUser" :params="confirmDeleteUserParams" @confirmation-dialog-response="confirmDeleteUserHandler"></ConfirmationDialog>
        <SendOrgUserEmailDialog v-if="sendOrgUserEmailDialog" :userData="userData" @send-org-user-email-dialog-response="sendOrgUserEmailHandler"></SendOrgUserEmailDialog>
    </div>
</template>

<script>
    import { mapState, mapActions, mapMutations } from 'vuex'
    import { userRoles } from '@/store/users'
    import { maxAppLevels } from '@/store/users'
    import ConfirmationDialog from '@/components/confirmation-dialog'
    import SendOrgUserEmailDialog from '@/components/send-org-user-email-dialog'

    export default
    {
        name: "org-list-users",
        components:
        {
            ConfirmationDialog,
            SendOrgUserEmailDialog
        },
        data: function ()
        {
            return {
                tipDisabling:
                {
                    viewing: false,
                    editing: false,
                    deleting: false,
                    emailing: false
                },
                dialog: false,
                waitingOnServer: false,
                pagination: {},
                search: '',
                headers:
                [
                    {
                        text: 'Actions',
                        width: '150px',
                        value: 'actions',
                        sortable: false
                    },
                    {
                        text: 'Display Name',
                        width: '200px',
                        value: 'displayName',
                        sortable: false
                    },
                    {
                        text: 'Email Address',
                        width: '200px',
                        value: 'emailAddress',
                        sortable: false
                    },
                    {
                        text: 'User ID',
                        value: 'uId',
                        sortable: false
                    },
                    {
                        text: 'Last Login',
                        width: '200px',
                        value: 'accessedDate',
                        sortable: false
                    },
                    {
                        text: 'Role',
                        value: 'authorities[0].role',
                        sortable: false
                    },
                    {
                        text: 'Version',
                        value: 'appVersion',
                        sortable: false
                    },
                    {
                        text: 'Pro Version',
                        width: '100px',
                        value: 'appProVersion',
                        sortable: false
                    },
                    {
                        text: 'Device',
                        width: '200px',
                        value: 'deviceName',
                        sortable: false
                    },
                    {
                        text: 'Pro Device',
                        width: '200px',
                        value: 'deviceProName',
                        sortable: false
                    },
                    {
                        text: 'OS',
                        value: 'osName',
                        sortable: false
                    },
                    {
                        text: 'Pro OS',
                        width: '100px',
                        value: 'osProName',
                        sortable: false
                    },
                    {
                        text: 'Browser Info',
                        width: '400px',
                        value: 'browserInfo',
                        sortable: false
                    }
                ],
                readonly: false,
                editedIndex: -1,
                userData: {},
                updateEmailAddress: false,
                defaultUserData:
                {
                    displayName: '',
                    emailAddress: ''
                },
                userRoles: userRoles,
                maxAppLevels: maxAppLevels,
                confirmDeleteUser: false,
                confirmDeleteUserParams:
                {
                    title: 'Delete User',
                    text: '',
                    positiveResponse: 'Yes',
                    negativeResponse: 'No',
                    context: ''
                },
                sendOrgUserEmailDialog: false
            }
        },
        created()
        {
            this.userData = Object.assign({}, this.defaultUserData)
        },
        destroyed()
        {
            this.CLEAR_USERS()
        },
        props:
        {
            orgId: String('')
        },
        computed:
        {
            ...mapState('auth',
            {
                currentUser: state => state.currentUser
            }),
            ...mapState('users',
            {
                users: state => state.users,
                totalRecords: state => state.totalRecords,
                totalPlrTestsForUser: state => state.totalPlrTestsForUser,
                totalNpcTestsForUser: state => state.totalNpcTestsForUser,
                testProtocolSuiteNames: state => state.testProtocolSuiteNames
            }),
            ...mapState('common',
            {
                waiting: state => state.waiting
            }),
            formTitle()
            {
                if (this.readonly)
                {
                    return "View User"
                }
                return this.editedIndex === -1 ? 'New User' : 'Edit User'
            }
        },
        watch:
        {
            orgId()
            {
                this.loadItems()
            },
            dialog (val)
            {
                if (!val)
                {
                    this.closeCommon()
                }
            }
        },
        methods:
        {
            ...mapActions('users', ['GET_ORG_USERS', 'SET_ORG_USER', 'DELETE_USER', 'GET_TEST_COUNT_FOR_USER', 'GET_TEST_PROTOCOL_SUITE_NAMES']),
            ...mapMutations('users', ['CLEAR_USERS', 'SET_PLR_TEST_COUNT_FOR_USER', 'SET_NPC_TEST_COUNT_FOR_USER']),
            ...mapMutations('common', ['WAITING', 'SHOW_SNACKBAR_ERROR', 'HIDE_SNACKBAR']),
            async handleSearch()
            {
                await this.loadItems()
            },
            async handleClearSearch()
            {
                this.search = '';
                await this.loadItems()
            },
            async loadItems()
            {
                if (this.orgId === '')
                {
                    return
                }
                try
                {
                    this.HIDE_SNACKBAR();
                    this.WAITING(true);
                    await this.GET_ORG_USERS({orgId: this.orgId, pagination: this.pagination, search: this.search})
                }
                catch (error)
                {
                    this.SHOW_SNACKBAR_ERROR(error.message);
                }
                this.WAITING(false)
            },
            currentItems(items)
            {
                if (this.pagination.page !== 1 && 0 === items.length)
                {
                    this.pagination.page -= 1
                }
            },
            async viewItem(item)
            {
                this.tipDisabling.viewing = true;
                this.readonly = true;
                this.updateEmailAddress = false;
                try
                {
                    this.HIDE_SNACKBAR();
                    this.waitingOnServer = true;
                    await this.GET_TEST_COUNT_FOR_USER({uId: item.uId})
                }
                catch (error)
                {
                    this.SHOW_SNACKBAR_ERROR(error.message);
                }
                this.waitingOnServer = false;
                await this.commonViewEditItem(item)
            },
            async editItem(item)
            {
                this.tipDisabling.editing= true;
                this.readonly = false;
                this.SET_PLR_TEST_COUNT_FOR_USER(0);
                this.SET_NPC_TEST_COUNT_FOR_USER(0);
                await this.commonViewEditItem(item)
            },
            async commonViewEditItem(item)
            {
                await this.loadProtocolSuites();
                this.editedIndex = this.users.indexOf(item);
                this.userData = Object.assign({}, item);
                this.userData.role = item.authorities[0].role;
                this.userData.maxAppLevel = item.maxAppLevel;
                this.userData.testProtoSuite = item.testProtoSuite;
                this.dialog = true;
            },
            async deleteItem(item)
            {
                this.tipDisabling.deleting = true;
                this.confirmDeleteUserParams.text = `Are you sure you want to delete user: ${item.displayName}?`;
                this.confirmDeleteUserParams.context = item;
                this.confirmDeleteUser = true
            },
            async confirmDeleteUserHandler(payload)
            {
                this.confirmDeleteUser = false;
                // If the user should be deleted
                if (payload.response)
                {
                    try
                    {
                        this.HIDE_SNACKBAR();
                        this.WAITING(true);
                        await this.DELETE_USER({deleteIndex: this.users.indexOf(payload), uId: payload.context.uId});
                        this.loadItems()
                    }
                    catch (error)
                    {
                        this.SHOW_SNACKBAR_ERROR(error.message);
                    }
                    this.WAITING(false)
                }
            },
            closeDialog()
            {
                this.dialog = false
            },
            closeCommon()
            {
                this.waitingOnServer = false;
                setTimeout(() =>
                {
                    this.userData = Object.assign({}, this.defaultUserData);
                    this.readonly = false;
                    this.editedIndex = -1
                }, 300)
            },
            async save()
            {
                this.HIDE_SNACKBAR();
                this.waitingOnServer = true;
                this.readonly = true;
                // If the user is being edited
                if (this.editedIndex > -1)
                {
                    try
                    {
                        // If the user has None set for their testing protocol suite, change back to null
                        if ('None' === this.userData.testProtoSuite)
                        {
                            this.userData.testProtoSuite = null
                        }
                        if (!this.updateEmailAddress)
                        {
                            this.userData.emailAddress = undefined
                        }
                        await this.SET_ORG_USER({orgId: this.orgId, editedIndex: this.editedIndex, data: this.userData});
                        this.updateEmailAddress = false
                    }
                    catch (error)
                    {
                        this.waitingOnServer = false;
                        this.readonly = false;
                        this.SHOW_SNACKBAR_ERROR(error.message);
                        return
                    }
                }
                else // Else this is a new user
                {
                    try
                    {
                        // If the user is an org admin, set the user role to user since they cannot select it themselves
                        if (this.currentUser && this.currentUser.authorities && this.currentUser.authorities[0].role === 'ROLE_ORG_ADMIN')
                        {
                            this.userData.role = 'ROLE_USER'
                        }
                        await this.SET_ORG_USER({orgId: this.orgId, data: this.userData})
                    }
                    catch (error)
                    {
                        this.waitingOnServer = false;
                        this.readonly = false;
                        this.SHOW_SNACKBAR_ERROR(error.message);
                        return
                    }
                }
                this.dialog = false;
                this.loadItems()
            },
            sendEmailItem(item)
            {
                this.tipDisabling.emailing= true;
                this.userData = item;
                this.sendOrgUserEmailDialog = true
            },
            sendOrgUserEmailHandler()
            {
                this.sendOrgUserEmailDialog = false
            },
            async newUser()
            {
                await this.loadProtocolSuites()
            },
            async loadProtocolSuites()
            {
                // Only request the protocol suites once per session
                if (0 === this.testProtocolSuiteNames.length)
                {
                    try
                    {
                        this.HIDE_SNACKBAR();
                        this.waitingOnServer = true;
                        await this.GET_TEST_PROTOCOL_SUITE_NAMES()
                    }
                    catch (error)
                    {
                        this.SHOW_SNACKBAR_ERROR(error.message)
                    }
                    this.waitingOnServer = false
                }
            }
        }
    }
</script>

<style scoped>
</style>